import React, { useState, useEffect, useRef } from 'react'
import { Container, Row, Col, Form, Spinner, Table, OverlayTrigger, Tooltip, Button, Alert } from 'react-bootstrap'
import { useSearchParams } from 'react-router-dom'
import { stockItemAPI } from '../api/stockItemAPI'
import { getStockForecast } from '../api/stockForecastAPI'
import { handleSearchWithDebounce } from '../utils/tableUtils'
import { highlightText } from '../utils/commonUtils'
import InventoryStatusGraph from '../components/InventoryStatusGraph'
import InventoryTimelineGraph from '../components/InventoryTimelineGraph'
import { usePermissions } from '../contexts/PermissionContext'

function StockForecastScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const [searchParams] = useSearchParams()
    const stockItemId = searchParams.get('stockItemId')
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const [loading, setLoading] = useState(false)
    const [stockItems, setStockItems] = useState([])
    const [selectedItem, setSelectedItem] = useState(null)
    const [forecastData, setForecastData] = useState(null)
    const [forecastLoading, setForecastLoading] = useState(false)
    const [forecastError, setForecastError] = useState(null)
    const debounceTimeoutRef = useRef(null)

    // Fetch stock item by ID if provided in URL parameter
    useEffect(() => {
        async function fetchStockItemById() {
            if (!stockItemId) return

            try {
                setLoading(true)
                const item = await stockItemAPI.getById(stockItemId)
                setSelectedItem(item)
            } catch (error) {
                console.error('Error fetching stock item by ID:', error)
            } finally {
                setLoading(false)
            }
        }

        fetchStockItemById()
    }, [stockItemId])

    useEffect(() => {
        async function fetchStockItems() {
            if (!searchTerm) {
                setStockItems([])
                return
            }

            try {
                setLoading(true)
                const response = await stockItemAPI.getAll(
                    1, // page
                    20, // pageSize
                    searchTerm, // searchTerm
                    '', // manufacturerId
                    '', // categoryIds
                    false // showDeactivated
                )
                setStockItems(response.results)
            } catch (error) {
                console.error('Error fetching stock items:', error)
            } finally {
                setLoading(false)
            }
        }

        fetchStockItems()
    }, [searchTerm])

    useEffect(() => {
        async function fetchForecastData() {
            if (!selectedItem) {
                setForecastData(null)
                setForecastError(null)
                return
            }

            try {
                setForecastLoading(true)
                setForecastError(null)
                const data = await getStockForecast(selectedItem._id)
                setForecastData(data)
                // Log debug information
                console.group('Stock Forecast Debug Info')
                console.log('Selected Item:', selectedItem)
                console.log('Forecast Data:', data)
                console.groupEnd()
            } catch (error) {
                console.error('Error fetching forecast data:', error)
                setForecastError(error.toString())
            } finally {
                setForecastLoading(false)
            }
        }

        fetchForecastData()
    }, [selectedItem])

    const handleSearchChange = (e) => {
        const value = e.target.value
        setSearchInputValue(value)
        handleSearchWithDebounce(
            value,
            setSearchTerm,
            () => {}, // no need to set page
            debounceTimeoutRef
        )
    }

    const handleItemSelect = (item) => {
        setSelectedItem(item)
        setSearchInputValue('') // Clear search input when item is selected
        setSearchTerm('') // Clear search term when item is selected
        setStockItems([]) // Clear results when item is selected
    }

    const handleClear = () => {
        setSearchInputValue('')
        setSearchTerm('')
        setStockItems([])
        setSelectedItem(null)
        setForecastData(null)
        setForecastError(null)
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current)
        }
    }

    if (permissionsLoading) {
        return (
            <Container className="py-3">
                <div className="text-center">Loading...</div>
            </Container>
        )
    }

    if (!hasPermission('stock.forecast.view')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to view stock forecast.
                </Alert>
            </Container>
        )
    }

    return (
        <Container fluid className="px-4 py-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1>Stock Forecast</h1>
                </Col>
            </Row>

            {!stockItemId && (
                <Row className="mb-4">
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Select Stock Item (search by GTIN or description)</Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Control
                                    type="text"
                                    placeholder="Enter GTIN or description..."
                                    onChange={handleSearchChange}
                                    value={searchInputValue}
                                    autoComplete="off"
                                    spellCheck="false"
                                />
                                {(searchInputValue || selectedItem) && (
                                    <Button
                                        variant="outline-secondary"
                                        onClick={handleClear}
                                    >
                                        Clear
                                    </Button>
                                )}
                            </div>
                        </Form.Group>

                    {loading && (
                        <div className="text-center py-4">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                        </div>
                    )}

                    {!loading && stockItems.length > 0 && (
                        <div className="mt-3">
                            <Table hover>
                                <thead>
                                    <tr>
                                        <th>Image</th>
                                        <th>GTIN</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stockItems.map(item => (
                                        <tr 
                                            key={item._id}
                                            onClick={() => handleItemSelect(item)}
                                            style={{ 
                                                cursor: 'pointer',
                                                height: '60px' // Set consistent row height
                                            }}
                                            className="hover-highlight"
                                        >
                                            <td style={{ 
                                                width: '60px',
                                                height: '60px',
                                                verticalAlign: 'middle',
                                                textAlign: 'center'
                                            }}>
                                                {item.image ? (
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip>
                                                                <img 
                                                                    src={item.image} 
                                                                    alt="Stock Item"
                                                                    style={{
                                                                        width: '300px',
                                                                        height: '300px',
                                                                        objectFit: 'contain',
                                                                        border: '2px solid #122A5A',
                                                                        borderRadius: '4px',
                                                                        boxShadow: '0 4px 8px rgba(18, 42, 90, 0.1)',
                                                                        transition: 'transform 0.2s ease-in-out',
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <img 
                                                            src={item.image} 
                                                            alt="Stock Item Thumbnail"
                                                            style={{
                                                                width: '50px',
                                                                height: '50px',
                                                                objectFit: 'contain'
                                                            }}
                                                        />
                                                    </OverlayTrigger>
                                                ) : (
                                                    <div style={{
                                                        width: '50px',
                                                        height: '50px',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundColor: '#f8f9fa',
                                                        border: '1px solid #dee2e6',
                                                        borderRadius: '4px'
                                                    }}>
                                                        <i className="bi bi-image" style={{ fontSize: '1.5rem', color: '#6c757d' }}></i>
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ verticalAlign: 'middle' }}>{highlightText(item.gtin, searchTerm)}</td>
                                            <td style={{ verticalAlign: 'middle' }}>{highlightText(item.description, searchTerm)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {!loading && searchInputValue && stockItems.length === 0 && (
                        <div className="alert alert-info mt-3">
                            No items found matching your search.
                        </div>
                    )}
                </Col>
            </Row>
            )}

            {selectedItem && (
                <Row>
                    <Col>
                        <div className="alert alert-success">
                            <h4>Selected Item:</h4>
                            {selectedItem.image && (
                                <img 
                                    src={selectedItem.image} 
                                    alt="Stock Item"
                                    style={{
                                        width: '100px',
                                        height: '100px',
                                        objectFit: 'contain',
                                        marginBottom: '10px'
                                    }}
                                />
                            )}
                            <p><strong>GTIN:</strong> {selectedItem.gtin}</p>
                            <p><strong>Description:</strong> {selectedItem.description}</p>
                        </div>

                        {forecastLoading && (
                            <div className="text-center py-4">
                                <Spinner animation="border" role="status">
                                    <span className="visually-hidden">Loading forecast data...</span>
                                </Spinner>
                            </div>
                        )}

                        {forecastError && (
                            <div className="alert alert-danger">
                                <h4>Error Loading Forecast Data:</h4>
                                <p>{forecastError}</p>
                            </div>
                        )}

                        {forecastData && (
                            <div className="mt-4">
                                <InventoryStatusGraph data={forecastData} />
                                <InventoryTimelineGraph data={forecastData} />
                            </div>
                        )}
                    </Col>
                </Row>
            )}
        </Container>
    )
}

export default StockForecastScreen 